import { ColumnPhysicalType, ColumnType, defaultSupportedColumnTypes, sqlBinaryPhysicalTypes } from 'app/shared/utils/column-type.util';
import {
    disableMetricsCollectionCheckboxMetadata,
    maxConcurrentConnectionsMetadata,
    queryTimeoutMetadata,
    isolationLevelMetadata,
    getSqlParallelReadMetadataConsts,
    autoTableCreationMetadata,
    sqlWriteBatchTimeoutMetadata,
    sqlWriteBatchSizeMetadata,
    preCopyScriptMetadata
} from '../consts/metadata-consts';
import { IDatasetTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataset-type-metadata';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { sqlServerBinaryLengthMap, sqlServerPrecisionMap, sqlServerScaleMap } from './sqlserver-metadata';
import { UpdateMethod } from 'shell-models-lib';
import { getAppendOption, getSCD2Option, getUpsertOption } from 'app/registries/consts/copy-job-const';

export const sqlDWSupportedPhysicalTypes: ColumnPhysicalType[] = [
    ColumnPhysicalType.Bit,
    ColumnPhysicalType.Tinyint,
    ColumnPhysicalType.Smallint,
    ColumnPhysicalType.Int,
    ColumnPhysicalType.Bigint,
    ColumnPhysicalType.Smalldatetime,
    ColumnPhysicalType.Date,
    ColumnPhysicalType.Datetime,
    ColumnPhysicalType.Datetime2,
    ColumnPhysicalType.Datetimeoffset,
    ColumnPhysicalType.Time,
    ColumnPhysicalType.Smallmoney,
    ColumnPhysicalType.Money,
    ColumnPhysicalType.Decimal,
    ColumnPhysicalType.Numeric,
    ColumnPhysicalType.Float,
    ColumnPhysicalType.Real,
    ColumnPhysicalType.Uniqueidentifier,
    ColumnPhysicalType.Varbinary,
    ColumnPhysicalType.Char,
    ColumnPhysicalType.Nvarchar,
    ColumnPhysicalType.Varchar,
    ColumnPhysicalType.Binary,
    ColumnPhysicalType.Nchar
];

export const sqlDWLogicalToPhysicalMapping: { [key: string]: string } = {
    int16: ColumnPhysicalType.Smallint,
    int32: ColumnPhysicalType.Int,
    int64: ColumnPhysicalType.Bigint,
    byte: ColumnPhysicalType.Tinyint,
    'byte[]': ColumnPhysicalType.Varbinary,
    // eslint-disable-next-line id-blacklist
    boolean: ColumnPhysicalType.Bit,
    // eslint-disable-next-line id-blacklist
    string: ColumnPhysicalType.Nvarchar,
    date: ColumnPhysicalType.Date,
    datetime: ColumnPhysicalType.Datetime2,
    datetimeoffset: ColumnPhysicalType.Datetimeoffset,
    decimal: ColumnPhysicalType.Decimal,
    'char[]': ColumnPhysicalType.Nvarchar,
    single: ColumnPhysicalType.Real,
    timespan: ColumnPhysicalType.Time,
    guid: ColumnPhysicalType.Uniqueidentifier,
    double: ColumnPhysicalType.Float,
    xml: ColumnPhysicalType.Varchar,
    sbyte: ColumnPhysicalType.Smallint,
    uint16: ColumnPhysicalType.Int,
    uint32: ColumnPhysicalType.Bigint,
    uint64: ColumnPhysicalType.Bigint
};

export const sqlDwPhysicalToLogicalMapping: { [key: string]: string | { [key: string]: string } } = {
    bit: ColumnType.Boolean,
    tinyint: ColumnType.Byte,
    smallint: ColumnType.Int16,
    int: ColumnType.Int32,
    bigint: ColumnType.Int64,
    smalldatetime: ColumnType.DateTime,
    date: {
        default: ColumnType.DateTime,
        Parquet: ColumnType.Date
    },
    datetime: ColumnType.DateTime,
    datetime2: ColumnType.DateTime,
    datetimeoffset: ColumnType.DateTimeOffset,
    time: ColumnType.TimeSpan,
    smallmoney: ColumnType.Decimal,
    money: ColumnType.Decimal,
    decimal: ColumnType.Decimal,
    numeric: ColumnType.Decimal,
    float: ColumnType.Double,
    real: ColumnType.Single,
    uniqueidentifier: ColumnType.Guid,
    binary: ColumnType.ByteArray,
    varbinary: ColumnType.ByteArray,
    nchar: ColumnType.String,
    char: ColumnType.String,
    nvarchar: ColumnType.String,
    varchar: ColumnType.String,
    rowversion: ColumnType.ByteArray
};

const physicalToPhysicalMapping: { [key: string]: string } = {
    timestamp: ColumnPhysicalType.Varbinary,
    xml: ColumnPhysicalType.Varchar,
    geography: ColumnPhysicalType.Varbinary,
    geometry: ColumnPhysicalType.Varbinary,
    hierarchyid: ColumnPhysicalType.Nvarchar,
    image: ColumnPhysicalType.Varbinary,
    text: ColumnPhysicalType.Varchar,
    ntext: ColumnPhysicalType.Nvarchar
};

export const sqlDWPhysicalToPhysicalMapping: { [key: string]: { [key: string]: string } } = {
    AzureSqlTable: physicalToPhysicalMapping,
    AzureSqlMITable: physicalToPhysicalMapping,
    SqlServerTable: physicalToPhysicalMapping,
    AmazonRdsForSqlServerTable: physicalToPhysicalMapping
};

export const azureSqlDWDatasetMetadata: IDatasetTypeMetadata = {
    type: adfModelTypeMap.azureSqlDW.datasetType,
    supportLinkedServiceTypes: [adfModelTypeMap.azureSqlDW.linkedServiceType],
    displayName: stringResources.AzureSynapseAnalytics,
    datasetNamePrefix: 'AzureSynapseAnalyticsTable',
    tooltip: __IS_A365__ ? stringResources.AzureSqlDW_Tooltip : '',
    datasetKind: 'table',
    isSource: true,
    isSink: true,
    icon: {
        className: 'svg-i-100x100-SQLPool'
    },
    supportAutoTableCreation: true,
    supportDataFlowStoreProcedure: true,
    isFirstClass: true,
    supportGetMetadataActivity: true,
    supportDataTypes: defaultSupportedColumnTypes.concat(ColumnType.Date),
    supportEditPhysicalType: __IS_FABRIC_DPE__,
    isDataflowCompatible: true,
    supportDataFlowSink: true,
    supportDataFlowSource: true,
    supportCreatedAsV2: true,
    // support type conversion as sink if:
    // 1. copy method is not copy command or polybase
    // 2. or, copy method is copy command or polybase, but is in Trident & staging is enabled
    notSupportTypeConversionWhenSink: true,
    supportTypeConversionWhenStaging: __IS_FABRIC_DPE__,
    splitTablePath: true,
    category: ['Azure'],
    supportParallelRead: true,
    copySourceControls: [queryTimeoutMetadata].concat([
        isolationLevelMetadata,
        getSqlParallelReadMetadataConsts(adfModelTypeMap.azureSqlDW.copySourceType)
    ]),
    copySinkControls: [
        autoTableCreationMetadata(true),
        preCopyScriptMetadata(),
        sqlWriteBatchTimeoutMetadata,
        sqlWriteBatchSizeMetadata,
        maxConcurrentConnectionsMetadata,
        disableMetricsCollectionCheckboxMetadata
    ],
    LogicalToPhysicalMapping: sqlDWLogicalToPhysicalMapping,
    PhysicalToLogicalMapping: sqlDwPhysicalToLogicalMapping,
    supportedPhysicalTypes: sqlDWSupportedPhysicalTypes,
    physicalToPhysicalMapping: sqlDWPhysicalToPhysicalMapping,
    binaryPhysicalTypes: sqlBinaryPhysicalTypes,
    binaryLengthMap: sqlServerBinaryLengthMap,
    precisionMap: sqlServerPrecisionMap,
    scaleMap: sqlServerScaleMap,
    copySourcePropertiesRequireParameters: ['sqlReaderQuery', 'partitionOption'],
    copySinkPropertiesRequireParameters: ['preCopyScript', 'tableOption'],
    copyJob: {
        updateMethod: {
            supportUpdateMethod: true,
            options: [getAppendOption(), getUpsertOption(), getSCD2Option()],
            updateMethodProperties: ['writeBehavior', 'allowCopyCommand', 'upsertSettings', 'copyCommandSettings'],
            defaultUpdateMethod: UpdateMethod.Append,
            upsertKeyColumnRequired: true
        },
        fullLoadBehavior: {
            supportFullLoadBehavior: true
        },
        multipleQuery: true,
        sourcePartition: {
            supportSourcePartition: true
        }
    }
};
